import { HelpArticlesCard } from "@faire/help-center-self-serve/components/HelpArticlesCard";
import { LazyCallbackQueueModal } from "@faire/help-center-self-serve/components/Modals/CallbackQueueModal/LazyCallbackQueueModal";
import { useFaireDirectWidgetChat } from "@faire/help-center-self-serve/hooks/useFaireDirectWidgetChat";
import { getBrand } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailer } from "@faire/help-center-utils/serialized-data/getRetailer";
import { getRetailerOrBrandToken } from "@faire/help-center-utils/serialized-data/getRetailerOrBrandToken";
import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { Spacer } from "@faire/web--source/slate/spacing";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackCxFoundationsAnswerPageView } from "@faire/web-api--source/events/cxFoundations/view/answerPage";
import { QueryParameters as recommendedArticleParams } from "@faire/web-api--source/routes/www/support/self-serve/recommended-article";
import React, { Suspense, useEffect } from "react";

import { BackButton } from "../../components/BackButton";
import { CenteredLoadingSpinner } from "../../components/CenteredLoadingSpinner";
import { ComprehensiveCSATCollection } from "../../components/ComprehensiveCSATCollection";
import { InstantAnswerCard } from "../../components/InstantAnswerCard";
import { LazyMessageModal } from "../../components/Modals/MessageModal/LazyMessageModal";
import { LazyPayoutModal } from "../../components/Modals/PayoutModal/LazyPayoutModal";
import { useInstantAnswerContextAdditionalData } from "../../hooks/useInstantAnswerContextAdditionalData";
import { useIssueType } from "../../hooks/useIssueType";
import { useOrderToken } from "../../hooks/useOrderToken";
import { useScrollToTopOnLoadEffect } from "../../hooks/useScrollToTopEffect";
import { useSelectedBrandOrder } from "../../hooks/useSelectedBrandOrder";
import { useSelectedNodeAnswer } from "../../hooks/useSelectedNodeAnswer";

import { ContactOptions } from "./ContactOptions";
import { NoInstantAnswerSection } from "./NoInstantAnswerSection";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

export const Answer: React.FC = () => {
  useScrollToTopOnLoadEffect();
  useFaireDirectWidgetChat();

  const [lastVisitedArticle, setLastVisitedArticle] = useState<string>("");
  const { issueType } = useIssueType();
  const { orderToken } = useOrderToken();
  const { selectedBrandOrder } = useSelectedBrandOrder();
  const { hasInstantAnswer, instantAnswer, hasHelpArticles, helpArticles } =
    useSelectedNodeAnswer();
  const { instantAnswerContextAdditionalData, asyncDataComplete } =
    useInstantAnswerContextAdditionalData(instantAnswer);

  const retailer = getRetailer();
  const brand = getBrand();
  const retailerOrBrandToken = getRetailerOrBrandToken();
  const { isMobile } = useViewport();

  const { message_modal, payout_update_modal, call_request_modal } =
    useQueryParams(recommendedArticleParams.parse);

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "help_center-page_self_serve_answer",
    numComponents: instantAnswer ? 3 : 2,
    enableTrackPerformanceBreakdown: "internal-tracking",
    pageLoadKey: "help_center-page_self_serve_answer",
    isPageLoading: false,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
  });

  useEffect(() => {
    trackCxFoundationsAnswerPageView(retailerOrBrandToken, issueType);
  }, [retailerOrBrandToken, issueType]);

  if (!asyncDataComplete) {
    return <CenteredLoadingSpinner />;
  }

  return (
    <div data-test-id="answer-wrapper">
      <BackButton />
      {isMobile ? null : <Spacer height="4x" />}
      {instantAnswer ? (
        <>
          <InstantAnswerCard
            instantAnswer={instantAnswer}
            viewHasHelpCenterArticles={hasHelpArticles}
            brandOrder={selectedBrandOrder}
            brand={brand}
            retailer={retailer}
            issueType={issueType}
            {...instantAnswerContextAdditionalData}
            loadTimingRef={loadTimingRef}
          />
          <Spacer height="3x" />
        </>
      ) : null}
      {!hasInstantAnswer && hasHelpArticles ? <NoInstantAnswerSection /> : null}
      {hasHelpArticles ? (
        <>
          <HelpArticlesCard
            visibleWhen={hasHelpArticles}
            articles={helpArticles}
            viewHasInstantAnswer={hasInstantAnswer}
            onClickOnArticle={setLastVisitedArticle}
            loadTimingRef={loadTimingRef}
          />
          <Spacer height="2x" />
        </>
      ) : null}

      {hasInstantAnswer || hasHelpArticles ? (
        <>
          <ComprehensiveCSATCollection
            visibleWhen={hasInstantAnswer || hasHelpArticles}
            issue={issueType}
            brandOrRetailerToken={getRetailerOrBrandToken()}
            isInstantAnswerAvailable={hasInstantAnswer}
            suggestedArticles={helpArticles.map(({ id }) => id)}
            lastVisitedArticle={lastVisitedArticle}
            brandOrderToken={orderToken}
          />
          {isMobile ? <Spacer height="2x" /> : <Spacer height="5x" />}
        </>
      ) : null}
      <ContactOptions
        loadTimingRef={loadTimingRef}
        hasDescription={!hasInstantAnswer && !hasHelpArticles}
      />
      {message_modal === "1" ? (
        <Suspense fallback={null}>
          <LazyMessageModal
            brandOrder={selectedBrandOrder}
            brand={brand}
            retailer={retailer}
            {...instantAnswerContextAdditionalData}
          />
        </Suspense>
      ) : null}
      {payout_update_modal === "1" ? (
        <Suspense fallback={null}>
          <LazyPayoutModal
            brandOrder={selectedBrandOrder}
            brand={brand}
            retailer={retailer}
            {...instantAnswerContextAdditionalData}
          />
        </Suspense>
      ) : null}
      {call_request_modal === "1" ? (
        <Suspense fallback={null}>
          <LazyCallbackQueueModal />
        </Suspense>
      ) : null}
    </div>
  );
};
